import React, { useCallback, useEffect, useRef, useState } from "react";
import { MOTION } from "../../styles/motionTokens";
import { subscribeTradeMatches } from "../../trades/tradeMatchSignal";

export const NEW_MATCHES_HOLD_MS = 4200;

/** "3 New Matches!" / "1 New Match!" */
export const newMatchesLabel = (delta) => {
  const n = Math.max(0, Number(delta) || 0);
  return `${n} New Match${n === 1 ? "" : "es"}!`;
};

/**
 * NewMatchesToast — a pill that drops in when the map finds new trade matches.
 *
 * Listens to the same signal that wobbles the Trades nav icon, so the two
 * always agree: a number appears only when something genuinely new arrived.
 * Self-dismisses; tapping it hands control back to the caller (usually to focus
 * the matches on the map).
 */
const NewMatchesToast = ({
  onClick,
  onVisibilityChange,
  holdMs = NEW_MATCHES_HOLD_MS,
  topOffset = 58,
}) => {
  const [state, setState] = useState(null); // { delta, count } | null
  const timer = useRef(null);

  // Kept in a ref so the subscription below never needs to resubscribe when the
  // caller passes a fresh inline callback.
  const visibilityRef = useRef(onVisibilityChange);
  useEffect(() => {
    visibilityRef.current = onVisibilityChange;
  }, [onVisibilityChange]);

  useEffect(() => {
    const unsubscribe = subscribeTradeMatches(({ count, delta }) => {
      setState({ count, delta });
      visibilityRef.current?.(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setState(null);
        visibilityRef.current?.(false);
      }, holdMs);
    });
    return () => {
      unsubscribe();
      clearTimeout(timer.current);
      // The toast is mounted conditionally (map view only), so unmounting mid-hold
      // is a real exit path — and the top slot is exclusive. Reporting it here too
      // is what stops a toast that no longer exists from owning the slot for the
      // rest of the session, which suppressed the opportunity callout, the session
      // pill, the roaming banner, the nearby pill and the status banner.
      visibilityRef.current?.(false);
    };
  }, [holdMs]);

  const dismiss = useCallback(() => {
    clearTimeout(timer.current);
    setState(null);
    visibilityRef.current?.(false);
  }, []);

  if (!state) return null;

  return (
    <button
      type="button"
      data-testid="new-matches-toast"
      data-match-delta={state.delta}
      onClick={() => {
        dismiss();
        onClick?.(state);
      }}
      style={{
        position: "absolute",
        top: topOffset,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 11,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 16px",
        borderRadius: 999,
        border: "1px solid rgba(196,181,253,0.45)",
        background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)",
        color: "#fff",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: "nowrap",
        cursor: "pointer",
        boxShadow: "0 8px 26px rgba(109,40,217,0.5), 0 0 18px rgba(167,139,250,0.4)",
        animation: `newMatchesDrop ${MOTION.sheetSlideMs}ms cubic-bezier(0.18, 0.9, 0.24, 1.1) both`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "#f0abfc",
          boxShadow: "0 0 8px 2px rgba(240,171,252,0.9)",
        }}
      />
      {newMatchesLabel(state.delta)}

      <style>{`
        @keyframes newMatchesDrop {
          from { opacity: 0; transform: translateX(-50%) translateY(-14px) scale(0.94); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="new-matches-toast"] { animation: none !important; }
        }
      `}</style>
    </button>
  );
};

export default NewMatchesToast;
